// --- MatchStateViewer.tsx ---
import { MatchState, RoundResult } from '@alienheadwars/football-card-manager-model';
// Import necessary MUI components and icons
import ExitToAppIcon from '@mui/icons-material/ExitToApp'; // Example Icon
import { Dialog, DialogContent, DialogContentText, DialogTitle, IconButton, Stack } from '@mui/material'; // Removed DialogActions
import React, { useEffect, useState } from "react";
// --- Import ChoiceButtonsComponent and ButtonComponent ---
import { getCallableFunction } from '../../../../firebase/firebase'; // Adjust path relative to MatchStateViewer.ts as needed
import { ButtonComponent } from '../../../base/button/Button'; // Keep for Finish Game button
import { ChoiceButtonsComponent } from '../../../base/button/ChoiceButtons'; // Import ChoiceButtonsComponent
import './Match.css';
import { ScoreboardComponent } from "./Scoreboard";
import { Contest } from './challenge/Contest';
import { RoundStateComponent } from './round/RoundStateComponent';
// --- Import localise ---
import { localise } from '../../../../locale/locale'; // Adjust path as needed
import { AnimatedCard } from '../../../MotionMui/MotionMui';


export interface HasMatchState {
    matchState: MatchState
}

export interface MatchStateProps extends HasMatchState {
    onComplete(): void;
    playerTeamId: string; // Add player's team ID
}

export const MatchStateComponent = (props: MatchStateProps) => {
    const { matchState, playerTeamId } = props;
    const [currentRound, setCurrentRound] = useState(matchState.rounds.length - 1);
    const [showContest, setShowChallenge] = useState(false);
    const [curentRoundStateToView, setCurentRoundStateToView] = useState(matchState.rounds[currentRound]);
    // --- State for confirmation dialog ---
    const [showConfirmQuit, setShowConfirmQuit] = useState(false);
    const [isQuitting, setIsQuitting] = useState(false); // To disable button during call

    useEffect(() => {
        //--- Existing useEffect logic ---
        const currentRoundState = matchState.rounds[currentRound];
        const roundFinished = currentRoundState.roundFinished === true
        if (roundFinished) {
            if ([RoundResult.ATTACK_STALL, RoundResult.PASS_UNCONTESTED, RoundResult.RUN_UNCONTESTED, RoundResult.BOTH_STALL].includes(currentRoundState.roundResult!)) {
                setCurentRoundStateToView(matchState.rounds[currentRound])
            }
            else {
                setShowChallenge(true)
            }

        }
        else {
            setCurentRoundStateToView(currentRoundState)
        }
        // --- Reset quit confirmation if match finishes normally ---
        if (matchState.matchFinished) {
            setShowConfirmQuit(false);
        }
    }, [currentRound, matchState.rounds, matchState.matchFinished])

    const nextRound = () => setCurrentRound((round) => {
        const newRound = Math.min(round + 1, matchState.rounds.length - 1)
        setCurentRoundStateToView(matchState.rounds[newRound])
        return newRound
    })

    const completeViewChallenge = () => {
        setCurentRoundStateToView(matchState.rounds[currentRound])
        setShowChallenge(false)
    }

    // --- Handlers for quit confirmation ---
    const handleOpenConfirmQuit = () => {
        setShowConfirmQuit(true);
    };

    const handleCloseConfirmQuit = () => {
        if (isQuitting) return; // Prevent closing while action is in progress
        setShowConfirmQuit(false);
    };

    const handleConfirmQuit = async () => {
        if (isQuitting) return; // Prevent multiple calls
        setIsQuitting(true);
        try {
            const resignMatchFunction = getCallableFunction('resignMatch');
            await resignMatchFunction({ matchId: matchState.id });
            setShowConfirmQuit(false);
        } catch (error) {
            console.error("Error resigning match:", error);
            // Handle error display to user if needed
        } finally {
            setIsQuitting(false);
        }
    };


    return <Stack direction="column"
        sx={{ width: '100%', height: '100%', position: 'relative' }}
        alignContent='center'
        justifyContent='flex-start' alignItems='center'>

        {/* --- Quit Button --- */}
        {!matchState.matchFinished && (
            <IconButton
                aria-label="quit match"
                onClick={handleOpenConfirmQuit}
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    color: 'white', // Adjust color as needed
                    zIndex: 10
                }}
                disabled={isQuitting}
            >
                <ExitToAppIcon />
            </IconButton>
        )}


        {showContest && <Contest
            onClick={completeViewChallenge}
            roundState={matchState.rounds[currentRound]}
            playerTeamId={playerTeamId}
            teams={matchState.teams}
        />}
        <AnimatedCard sx={{ marginBottom: '1vmin', backgroundColor:'rgb(0,0,0,0.8)', color:'white'}}>
            <ScoreboardComponent matchState={matchState} />
        </AnimatedCard>
        <RoundStateComponent
            roundState={curentRoundStateToView}
            finishRound={nextRound}
            playerTeamId={playerTeamId}
            matchId={matchState.id}
            teams={matchState.teams}
        />
        {matchState.matchFinished && <ButtonComponent onClick={props.onComplete}>Finish Game</ButtonComponent>}

        {/* --- UPDATED: Confirmation Dialog with ChoiceButtonsComponent --- */}
        <Dialog
            open={showConfirmQuit}
            onClose={handleCloseConfirmQuit} // Use the handler that checks isQuitting
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                {localise('dialog.quitMatch.title')} {/* Example locale key */}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}> {/* Added margin bottom */}
                    {localise('dialog.quitMatch.description')} {/* Example locale key */}
                </DialogContentText>
                {/* --- Use ChoiceButtonsComponent --- */}
                <ChoiceButtonsComponent
                    primaryButtonText={localise('dialog.quitMatch.confirmButton')} // Example locale key
                    primaryButtonAction={handleConfirmQuit}
                    primaryButtonDisabled={isQuitting}
                    secondaryButtonText={localise('dialog.quitMatch.cancelButton')} // Example locale key 
                    secondaryButtonAction={handleCloseConfirmQuit}
                    secondaryButtonDisabled={isQuitting}
                />
            </DialogContent>
            {/* Removed <DialogActions> */}
        </Dialog>

    </Stack>

};